import React, { createContext, useContext } from 'react';
import axios from 'axios';

const ApiContext = createContext();

export function useApi() {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
}

// API Configuration  
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://blueteam-securebank-api.globalsoc.workers.dev';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('blueteam_user') || '{}');
    if (user.id) {
      config.headers.Authorization = `Bearer ${user.id}`;
    }
    
    // Remove Content-Type for FormData uploads (let axios set it automatically)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('blueteam_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export function ApiProvider({ children }) {
  
  // Health check
  const healthCheck = async () => {
    try {
      const response = await apiClient.get('/api/health');
      return response.data;
    } catch (error) {
      throw new Error(`Health check failed: ${error.message}`);
    }
  };

  // Dashboard API
  const getDashboardStats = async () => {
    try {
      const response = await apiClient.get('/api/dashboard/stats');
      return response.data;
    } catch (error) {
      console.error('Dashboard stats error:', error);
      // Return mock data as fallback
      return {
        total_controls: 234,
        mapped_controls: 154,
        coverage_percentage: 65.8,
        clouds: { Azure: 93, GCP: 70, IBM: 40, OCI: 31 },
        recent_activity: []
      };
    }
  };

  // Controls API
  const getControls = async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await apiClient.get(`/api/controls?${params}`);
      return response.data;
    } catch (error) {
      console.error('Get controls error:', error);
      return { controls: [], total: 0, has_more: false };
    }
  };

  const analyzeControl = async (controlData) => {
    try {
      const response = await apiClient.post('/api/controls/analyze', controlData);
      return response.data;
    } catch (error) {
      console.error('Analyze control error:', error);
      throw error;
    }
  };

  const uploadControls = async (file, orgId = 'securebank') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('org_id', orgId);

      const response = await apiClient.post('/api/controls/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Progress tracking for upload
          if (percentCompleted % 10 === 0) {
            console.error(`Upload progress: ${percentCompleted}%`);
          }
        }
      });
      return response.data;
    } catch (error) {
      console.error('Upload controls error:', error);
      throw error;
    }
  };

  // Mapping API
  const getMappingSuggestions = async (controlData) => {
    try {
      const response = await apiClient.post('/api/mapping/suggest', controlData);
      return response.data;
    } catch (error) {
      console.error('Mapping suggestions error:', error);
      return { suggestions: [], confidence: 0 };
    }
  };

  // Bulk mapping with extended timeout
  const bulkAnalyze = async (data) => {
    try {
      const response = await apiClient.post('/api/mapping/bulk-analyze', data, {
        timeout: 300000 // 5 minutes for bulk operations
      });
      return response.data;
    } catch (error) {
      console.error('Bulk analyze error:', error);
      throw error;
    }
  };

  // Search API
  const semanticSearch = async (query, namespace = 'controls', topK = 10) => {
    try {
      const response = await apiClient.post('/api/search/semantic', {
        query,
        namespace,
        topK
      });
      return response.data;
    } catch (error) {
      console.error('Semantic search error:', error);
      return { results: [], total: 0 };
    }
  };

  // Reports API
  const generateReport = async (reportType, options = {}) => {
    try {
      const response = await apiClient.post(`/api/reports/${reportType}`, {
        org_id: 'securebank',
        format: 'json',
        ...options
      });
      return response.data;
    } catch (error) {
      console.error('Generate report error:', error);
      throw error;
    }
  };

  // Mock data generators for development
  const getMockControls = () => [
    {
      id: 'ctrl-001',
      control_id: '1.2',
      title: 'Azure AD MFA Enforcement',
      description: 'Multi-factor authentication must be enabled for all privileged accounts in Azure AD',
      cloud_provider: 'Azure',
      category: 'Identity & Access Management',
      technique_id: 'T1078.004',
      confidence_score: 0.95,
      mapping_type: 'manual'
    },
    {
      id: 'ctrl-002',
      control_id: '1.3',
      title: 'GCP IAM Service Account Keys',
      description: 'Service account keys must be rotated every 90 days in GCP',
      cloud_provider: 'GCP',
      category: 'Identity & Access Management',
      technique_id: 'T1098.001',
      confidence_score: 0.85,
      mapping_type: 'automatic'
    },
    {
      id: 'ctrl-003',
      control_id: '2.1.5',
      title: 'IBM Cloud Storage Encryption',
      description: 'All data stored in IBM Cloud Object Storage must be encrypted at rest',
      cloud_provider: 'IBM',
      category: 'Data Protection',
      technique_id: 'T1530',
      confidence_score: 0.90,
      mapping_type: 'automatic'
    },
    {
      id: 'ctrl-004',
      control_id: '4.1',
      title: 'OCI Audit Logging',
      description: 'Comprehensive audit logging must be enabled for all OCI resources',
      cloud_provider: 'OCI',
      category: 'Logging & Monitoring',
      technique_id: 'T1562.001',
      confidence_score: 0.80,
      mapping_type: 'automatic'
    }
  ];

  const getMockAttackTechniques = () => [
    {
      technique_id: 'T1078.004',
      name: 'Valid Accounts: Cloud Accounts',
      description: 'Adversaries may obtain and abuse credentials of a cloud account',
      tactics: ['initial-access', 'persistence', 'privilege-escalation', 'defense-evasion'],
      platforms: ['Azure AD', 'Google Workspace', 'Office 365', 'SaaS'],
      cloud_platforms: ['Azure', 'GCP', 'AWS', 'IBM Cloud', 'OCI']
    },
    {
      technique_id: 'T1530',
      name: 'Data from Cloud Storage Object',
      description: 'Adversaries may access data objects from improperly secured cloud storage',
      tactics: ['collection'],
      platforms: ['Amazon S3', 'Azure Blob Storage', 'Google Cloud Storage'],
      cloud_platforms: ['Azure', 'GCP', 'AWS', 'IBM Cloud', 'OCI']
    }
  ];

  // Generic API helpers
  const apiGet = async (endpoint) => {
    try {
      const response = await apiClient.get(`/api${endpoint}`);
      return response.data;
    } catch (error) {
      console.error(`GET ${endpoint} error:`, error);
      throw error;
    }
  };

  const apiPost = async (endpoint, data) => {
    try {
      const response = await apiClient.post(`/api${endpoint}`, data);
      return response.data;
    } catch (error) {
      console.error(`POST ${endpoint} error:`, error);
      throw error;
    }
  };

  const value = {
    // Generic helpers
    apiGet,
    apiPost,
    
    // Specific API methods
    healthCheck,
    getDashboardStats,
    getControls,
    analyzeControl,
    uploadControls,
    getMappingSuggestions,
    bulkAnalyze,
    semanticSearch,
    generateReport,
    
    // Mock data for development
    getMockControls,
    getMockAttackTechniques,
    
    // Direct axios client for custom requests
    apiClient
  };

  return (
    <ApiContext.Provider value={value}>
      {children}
    </ApiContext.Provider>
  );
}